// Naive Kandidaten-Term-Extraktion aus dem Pitch-Text fuer die
// Tonalitaets-Suche (searchKeywords, siehe TobisLabPage). Keine KI hier -
// reine Tokenisierung; die eigentliche Bedeutungsarbeit macht searchKeywords
// selbst (Substring-Abgleich gegen das echte TMDB-Keyword-Vokabular, mit
// Rauschfilter im BFF). Fuer eine praezisere Tonalitaets-Extraktion aus
// Freitext ist der Chat-Assistent (ResolveTobisToneKeywords, agent-service,
// per LLM) die bessere Adresse - das Labor hier ist der schnelle,
// backend-lose erste Schritt ohne zusaetzlichen KI-Aufruf.

const STOPWORDS = new Set([
  // Deutsch
  "eine",
  "einen",
  "einem",
  "eines",
  "einer",
  "der",
  "die",
  "das",
  "und",
  "oder",
  "mit",
  "ohne",
  "fuer",
  "ueber",
  "unter",
  "kein",
  "keine",
  "sich",
  "sind",
  "ist",
  "auch",
  "sehr",
  "mehr",
  "wird",
  "werden",
  "hat",
  "haben",
  "aber",
  "wenn",
  "dann",
  "dass",
  "diese",
  "dieser",
  "dieses",
  // Englisch
  "the",
  "and",
  "or",
  "with",
  "without",
  "for",
  "over",
  "under",
  "this",
  "that",
  "these",
  "those",
  "about",
  "from",
  "into",
  "onto",
  "very",
  "more",
  "than",
  "then",
  "will",
  "have",
  "has",
  "are",
  "is",
  "but",
  "when",
]);

const MIN_TERM_LENGTH = 4;
const MAX_TERMS = 15;

/**
 * Zerlegt einen Personennamen in die Wörter, die als Kandidat nichts taugen.
 *
 * Der Anlass (Marvin, 2026-08-19): "Emma Stone" im Pitch brachte die Keywords
 * "stone", "stone age" und "philosopher's stone" - der Nachname ist ein
 * gewöhnliches englisches Wort, die Wortgrenzen-Regel im BFF greift dagegen
 * nicht (sie fängt nur Wortteile wie "hanks" in "thanksgiving").
 *
 * Bewusst ALLE Namensbestandteile und nicht nur der Nachname: Vornamen sind
 * genauso oft Wörter ("Grace", "Hope", "Sky"), und wer im Pitch eine Person
 * nennt, meint sie als Person, nicht als Thema.
 */
export function nameTokens(name: string | null | undefined): string[] {
  if (!name) return [];
  return (name.toLowerCase().match(/[a-zäöüß]+/g) ?? []).filter(
    (w) => w.length >= MIN_TERM_LENGTH,
  );
}

export function extractCandidateTerms(
  text: string,
  /**
   * Wörter, die kein Thema sind - in der Praxis Namensbestandteile der im Pitch
   * genannten oder im Formular gewählten Personen (siehe nameTokens). Sie
   * werden wie Stoppwörter behandelt.
   *
   * Der Preis dieser Regel: steht ein Name auch als echtes Motiv im Text ("ein
   * Stein in der Wüste" bei einer Schauspielerin Stone), fällt das Motiv mit
   * weg. Der umgekehrte Fehler ist häufiger und sichtbarer - deshalb so.
   */
  exclude: Iterable<string> = [],
): string[] {
  const excluded = new Set([...exclude].map((w) => w.toLowerCase()));
  const words = text.toLowerCase().match(/[a-zäöüß]+/g) ?? [];
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const word of words) {
    if (word.length < MIN_TERM_LENGTH || STOPWORDS.has(word) || excluded.has(word) || seen.has(word))
      continue;
    seen.add(word);
    terms.push(word);
    if (terms.length >= MAX_TERMS) break;
  }
  return terms;
}
